import { cn } from '@/lib/cn'

/**
 * The GMT 2027 mark: the gold sunburst with the Kaaba at its centre.
 *
 * The artwork carries its own rays, so the only decoration added here is a
 * soft corona glow behind it — a rotating ray sweep would fight the mark.
 *
 * Served at three sizes so a 36px header does not download a 512px image;
 * `sm` covers the header and footer, `lg` the hero and error page.
 */

const NATURAL = { width: 797, height: 730 }

const SOURCES = {
  sm: {
    src: '/brand/mark-gmt2027-128.png',
    srcSet: '/brand/mark-gmt2027-128.png 1x, /brand/mark-gmt2027-256.png 2x',
  },
  lg: {
    src: '/brand/mark-gmt2027-256.png',
    srcSet: '/brand/mark-gmt2027-256.png 1x, /brand/mark-gmt2027-512.png 2x',
  },
} as const

export function BrandMark({
  size = 'sm',
  className,
  glow = true,
  priority = false,
}: {
  size?: 'sm' | 'lg'
  className?: string
  glow?: boolean
  /** Set on the hero mark so it is not lazy-loaded below the fold. */
  priority?: boolean
}) {
  const source = SOURCES[size]

  return (
    <span className={cn('relative block', className)}>
      {glow ? (
        <span
          aria-hidden="true"
          className="absolute inset-[-18%] animate-[var(--animate-corona-pulse)] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.3)_0%,transparent_68%)]"
        />
      ) : null}
      <img
        src={source.src}
        srcSet={source.srcSet}
        alt=""
        width={NATURAL.width}
        height={NATURAL.height}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        decoding="async"
        className="relative block h-auto w-full"
      />
    </span>
  )
}
